/**
 * ⋯ **Print the crew sheet** — `docs/visit-page.md` §7.2 (*the crew iPad's
 * card and that day's load list, no money*). No board draws it: the lead's
 * ruling (Oct 29, a DEVIATION REQUEST — §7.2 says a PDF) is a print-ready
 * page, opened in a new tab and printed straight away; the browser's Print /
 * Save as PDF makes the PDF.
 *
 * `/visits/:id/crew-sheet?day=<n>` — frameless, like the crew's own screens.
 * The card is Slice 5's `CrewIpadCardView`, the load list Slice 2's By day
 * column, both from `GET /visits/:id/crew-sheet` (`CrewSheet`), which carries
 * no money for anybody.
 */
import { useEffect, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import type { CrewSheet } from '@rowted/shared'
import { EmptyState } from '../components/primitives.js'
import { useVisitTab } from './api.js'
import { CREW_SHEET, HEADER } from './copy.js'
import { CrewIpadCardView } from './crew-ipad-card.js'
import { DayColumn } from './materials-by-day.js'

export function CrewSheetPage() {
  const { id = '' } = useParams()
  const [params] = useSearchParams()
  const day = params.get('day')
  const { data, error } = useVisitTab<CrewSheet>(id, '/crew-sheet', { day })
  const printed = useRef(false)

  useEffect(() => {
    if (!data) return
    document.title = CREW_SHEET.title(data.reference, String(data.day.index))
    if (printed.current) return
    printed.current = true
    /* After the paint, so the sheet prints with its card and list drawn. */
    const frame = window.requestAnimationFrame(() => window.print())
    return () => window.cancelAnimationFrame(frame)
  }, [data])

  if (error) return <EmptyState title={HEADER.breadcrumb} body={error} />
  if (!data) return <div className="min-h-screen bg-surface" data-crew-sheet-loading="" />

  return (
    <main className="flex min-h-screen flex-col items-center gap-4 bg-surface px-4 py-6 print:p-0" data-crew-sheet="">
      <button
        type="button"
        onClick={() => window.print()}
        className="inline-flex h-11 items-center rounded-control border border-line bg-surface px-4 text-[13px] font-semibold text-text hover:bg-hover print:hidden"
      >
        {CREW_SHEET.print}
      </button>
      <CrewIpadCardView card={data.card} className="print:shadow-none" />
      {data.column && (
        <div className="flex w-full max-w-[416px] break-inside-avoid">
          <DayColumn
            column={data.column}
            read={{ timeZone: data.timeZone }}
            onOrder={() => {}}
            canEdit={false}
          />
        </div>
      )}
    </main>
  )
}
